/**
 * Typed access to the generated page catalogue.
 *
 * `pageCatalogue.json` is written by `scripts/access/genPageCatalogue.mjs`
 * (run on `npm start`, verified on `npm run build`). It walks `router.tsx` and
 * each page's import graph to record, per route path:
 *
 *   component    — the view component mounted at that path
 *   baselineOrgs — organisations whose router block mounts the page by code
 *                  (`*` = every signed-in user, e.g. /profile)
 *   endpoints    — backend API paths the page can call
 *
 * Never edit the JSON by hand; change the router/views and regenerate.
 */
import catalogue from './pageCatalogue.json'

export interface PageCatalogueEntry {
    component: string
    baselineOrgs: string[]
    endpoints: string[]
}

export const PAGE_CATALOGUE: Record<string, PageCatalogueEntry> = catalogue as Record<string, PageCatalogueEntry>

export const PAGE_PATHS: string[] = Object.keys(PAGE_CATALOGUE)

/** Orgs the overrides system never touches (they already see everything). */
export const OVERRIDE_EXEMPT_ORGANISATIONS = ['Sentinel']

/** Pages that can never be removed from an org (shell / self-service / this admin page). */
export const PROTECTED_PAGES = ['/users', '/profile']

/** True when `organisation` mounts `path` by code. */
export const isBaselinePage = (organisation: string, path: string): boolean => {
    const orgs = PAGE_CATALOGUE[path]?.baselineOrgs || []
    return orgs.includes('*') || orgs.includes(organisation)
}

/** Endpoints for a path (empty when the path is not in the catalogue). */
export const pageEndpoints = (path: string): string[] => PAGE_CATALOGUE[path]?.endpoints || []

/**
 * Endpoints that belong ONLY to `path` among `remainingPaths`. This is what a
 * `block` row removes server-side, so endpoints shared with pages the org
 * still has (e.g. /get-chart-dataset) keep working.
 */
export const exclusiveEndpoints = (path: string, remainingPaths: string[]): string[] => {
    const shared = new Set<string>()
    remainingPaths.forEach((p) => {
        if (p !== path) pageEndpoints(p).forEach((e) => shared.add(e))
    })
    return pageEndpoints(path).filter((e) => !shared.has(e))
}
